import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import './RunJSFrame.css'

// SVGs and Icons
function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
    </svg>
  )
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="12" height="12" rx="1" />
    </svg>
  )
}

function BookmarkIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  )
}

function ResetIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
    </svg>
  )
}

function TerminalIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="rjf-icon-check">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

function CircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="rjf-icon-circle">
      <circle cx="12" cy="12" r="10" />
    </svg>
  )
}

const T = (type, text) => ({ type, text })

const KEYWORDS = new Set([
  'const', 'let', 'var', 'function', 'return', 'if', 'else', 'for',
  'while', 'do', 'switch', 'case', 'break', 'continue', 'new', 'delete',
  'typeof', 'instanceof', 'class', 'extends', 'import', 'export', 'default',
  'from', 'async', 'await', 'yield', 'throw', 'try', 'catch', 'finally',
  'this', 'super', 'in', 'of', 'void', 'true', 'false', 'null', 'undefined',
])

function tokenize(code) {
  if (!code) return []
  const lines = code.split('\n')
  return lines.map(raw => {
    if (raw === '') return [T('op', '\u00A0')]
    const tokens = []
    let remaining = raw

    while (remaining.length > 0) {
      let m

      // Match whitespace explicitly so we preserve it and don't treat it character by character
      m = remaining.match(/^\s+/)
      if (m) {
        tokens.push(T('space', m[0]))
        remaining = remaining.slice(m[0].length)
        continue
      }

      if (remaining.startsWith('//')) {
        tokens.push(T('comment', remaining))
        break
      }

      if (remaining.startsWith('/*')) {
        const end = remaining.indexOf('*/', 2)
        if (end !== -1) {
          tokens.push(T('comment', remaining.slice(0, end + 2)))
          remaining = remaining.slice(end + 2)
          continue
        }
        tokens.push(T('comment', remaining))
        break
      }

      if (remaining.startsWith('`')) {
        const end = remaining.indexOf('`', 1)
        if (end !== -1) {
          tokens.push(T('str', remaining.slice(0, end + 1)))
          remaining = remaining.slice(end + 1)
          continue
        }
      }

      m = remaining.match(/^"([^"\\]|\\.)*"/)
      if (m) { tokens.push(T('str', m[0])); remaining = remaining.slice(m[0].length); continue }

      m = remaining.match(/^'([^'\\]|\\.)*'/)
      if (m) { tokens.push(T('str', m[0])); remaining = remaining.slice(m[0].length); continue }

      m = remaining.match(/^\d+(\.\d+)?/)
      if (m) { tokens.push(T('num', m[0])); remaining = remaining.slice(m[0].length); continue }

      m = remaining.match(/^\.([a-zA-Z_$][\w$]*)/)
      if (m) { tokens.push(T('fn', m[0])); remaining = remaining.slice(m[0].length); continue }

      m = remaining.match(/^([a-zA-Z_$][\w$]*)\s*(?=\()/)
      if (m) { tokens.push(T('fn', m[1])); remaining = remaining.slice(m[1].length); continue }

      m = remaining.match(/^[a-zA-Z_$][\w$]*/)
      if (m && KEYWORDS.has(m[0])) { tokens.push(T('kw', m[0])); remaining = remaining.slice(m[0].length); continue }

      if (m) { tokens.push(T('var', m[0])); remaining = remaining.slice(m[0].length); continue }

      m = remaining.match(/^[{}()[\];:.,+\-*/%=<>!&|^~?@#]+/)
      if (m) { tokens.push(T('op', m[0])); remaining = remaining.slice(m[0].length); continue }

      tokens.push(T('op', remaining[0]))
      remaining = remaining.slice(1)
    }

    return tokens
  })
}

// Typing animation constants
const STATIC_CODE = [
  "import http from 'http';",
  '',
  "const host = 'localhost';",
  "const port = 3000;",
  '',
  'const server = http.createServer((req, res) => {',
  '  res.writeHeader(200);',
  "  res.end('My first server!');",
  '});',
].join('\n')

const TYPED_CODE = [
  '',
  'server.listen(port, host, () => {',
  '  console.log(',
  // eslint-disable-next-line no-template-curly-in-string
  "    `Listening on http://${host}:${port}`",
  '  );',
  '});',
].join('\n')

function buildTypedChars(tokens) {
  const chars = []
  for (const t of tokens) {
    for (let i = 0; i < t.text.length; i++) {
      chars.push({ type: t.type, char: t.text[i] })
    }
  }
  return chars
}

function renderTokens(lineTokens, lineIdx) {
  if (!lineTokens) return <span key={`l${lineIdx}`} className="rjf-line empty" />
  return (
    <span key={`l${lineIdx}`} className="rjf-line">
      {lineTokens.map((t, j) => <span key={j} className={`rjf-${t.type}`}>{t.text}</span>)}
    </span>
  )
}

function renderStatic(tokenizedLines) {
  return tokenizedLines.map((line, i) => renderTokens(line, i))
}

const LESSONS = [
  {
    id: 1,
    template: `// Write your code here / Escribe tu código aquí\nconsole.log("Hello, World!");\n`,
    validate: (logs, code) => {
      return logs.some(log => /hello\s*,?\s*world/i.test(log.text) || /hola\s*,?\s*mundo/i.test(log.text));
    }
  },
  {
    id: 2,
    template: `const a = 5;\n// Declare variable 'b' with value 10 and print the sum (a + b)\n// Declara la variable 'b' con el valor 10 e imprime la suma (a + b)\n\n`,
    validate: (logs, code) => {
      const hasA = code.includes('a');
      const hasB = code.includes('b');
      const hasSum = logs.some(log => log.text.trim() === '15');
      return hasA && hasB && hasSum;
    }
  },
  {
    id: 3,
    template: `function multiply(x, y) {\n  // Return the product / Retorna el producto\n\n}\n\n// Call multiply with 6 and 7, and print it with console.log\n// Llama a multiply con 6 y 7, e imprímelo con console.log\n`,
    validate: (logs, code) => {
      const hasMultiply = code.includes('multiply');
      const hasResult = logs.some(log => log.text.trim() === '42');
      return hasMultiply && hasResult;
    }
  },
  {
    id: 4,
    template: `const numbers = [1, 2, 3, 4];\n\n// Double the numbers using map and log the result array\n// Duplica los números usando map e imprime el array resultante\n`,
    validate: (logs, code) => {
      const hasMap = code.includes('.map');
      const hasArrayLog = logs.some(log => {
        const txt = log.text.replace(/\s+/g, '');
        return txt.includes('[2,4,6,8]') || txt === '2,4,6,8' || txt === '2468';
      });
      return hasMap && hasArrayLog;
    }
  },
  {
    id: 5,
    template: `// Create a resolved promise or use async/await, then log 'Success!'\n// Crea una promesa resuelta o usa async/await, luego imprime 'Success!'\n`,
    validate: (logs, code) => {
      return logs.some(log => log.text.trim().toLowerCase() === 'success!');
    }
  },
  {
    id: 6,
    template: `class DatabaseConnection {\n  constructor() {\n    this.created = Date.now();\n  }\n  \n  static getInstance() {\n    // Implement Singleton pattern / Implementa el patrón Singleton\n\n  }\n}\n\n// Do not modify below / No modifiques abajo\nconst db1 = DatabaseConnection.getInstance();\nconst db2 = DatabaseConnection.getInstance();\nconsole.log(db1 === db2);\n`,
    validate: (logs, code) => {
      const hasClass = code.includes('DatabaseConnection');
      const hasMethod = code.includes('getInstance');
      const hasTrue = logs.some(log => log.text.trim() === 'true');
      return hasClass && hasMethod && hasTrue;
    }
  },
  {
    id: 7,
    template: `class Car {\n  constructor(brand) { this.brand = brand; }\n  drive() { console.log(\`Driving a \${this.brand} car\`); }\n}\nclass Bike {\n  constructor(brand) { this.brand = brand; }\n  drive() { console.log(\`Riding a \${this.brand} bike\`); }\n}\n\nclass VehicleFactory {\n  createVehicle(type, brand) {\n    // Return a Car or Bike instance depending on type ('car' | 'bike')\n    // Retorna una instancia de Car o Bike según el tipo ('car' | 'bike')\n\n  }\n}\n\n// Test / Prueba\nconst factory = new VehicleFactory();\nconst car = factory.createVehicle('car', 'Toyota');\nif (car) car.drive();\n`,
    validate: (logs, code) => {
      const hasFactory = code.includes('VehicleFactory');
      const hasDriveCar = logs.some(log => /driving\s+a\s+toyota\s+car/i.test(log.text));
      return hasFactory && hasDriveCar;
    }
  },
  {
    id: 8,
    template: `class Subject {\n  constructor() {\n    this.observers = [];\n  }\n  subscribe(fn) {\n    // Add callback / Añade la función callback\n\n  }\n  unsubscribe(fn) {\n    // Remove callback / Remueve la función callback\n\n  }\n  notify(data) {\n    // Execute all observer callbacks with data\n    // Ejecuta todos los observadores pasándoles data\n\n  }\n}\n\n// Test / Prueba\nconst s = new Subject();\nconst obs1 = val => console.log(\`Obs1: \${val}\`);\nconst obs2 = val => console.log(\`Obs2: \${val}\`);\ns.subscribe(obs1);\ns.subscribe(obs2);\ns.notify("changed");\n`,
    validate: (logs, code) => {
      const hasClass = code.includes('class Subject') || code.includes('Subject =');
      const hasObs1 = logs.some(log => log.text.includes('Obs1: changed'));
      const hasObs2 = logs.some(log => log.text.includes('Obs2: changed'));
      return hasClass && hasObs1 && hasObs2;
    }
  },
  {
    id: 9,
    template: `class CreditCardStrategy {\n  calculate(amount) { return amount * 0.03; }\n}\nclass PayPalStrategy {\n  calculate(amount) { return amount * 0.05; }\n}\n\nclass PaymentProcessor {\n  setStrategy(strategy) {\n    // Set active strategy / Define la estrategia activa\n\n  }\n  calculateFee(amount) {\n    // Calculate fee using active strategy / Calcula comisión usando estrategia activa\n\n  }\n}\n\n// Test / Prueba\nconst processor = new PaymentProcessor();\nprocessor.setStrategy(new CreditCardStrategy());\nconsole.log(processor.calculateFee(100)); // 3\nprocessor.setStrategy(new PayPalStrategy());\nconsole.log(processor.calculateFee(100)); // 5\n`,
    validate: (logs, code) => {
      const hasProcessor = code.includes('PaymentProcessor');
      const has3 = logs.some(log => log.text.trim() === '3');
      const has5 = logs.some(log => log.text.trim() === '5');
      return hasProcessor && has3 && has5;
    }
  },
  {
    id: 10,
    template: `function withLogger(fn) {\n  // Return a decorated function that logs "Calling [name]" and executes fn\n  // Retorna una función decorada que imprima "Calling [nombre]" y ejecute fn\n\n}\n\nconst add = (x, y) => x + y;\n// Decorate add function and call it / Decora la función add y llámala\n`,
    validate: (logs, code) => {
      const hasDecorator = code.includes('withLogger');
      const hasLog = logs.some(log => /calling\s+add/i.test(log.text) || /calling/i.test(log.text));
      const hasResult = logs.some(log => log.text.includes('15') || log.text.includes('5') || log.text.includes('10'));
      return hasDecorator && (hasLog || hasResult);
    }
  },
  {
    id: 11,
    template: `// Implement a curried function multiply(a)(b)(c) that returns the product\n// Implementa una función currificada multiply(a)(b)(c) que devuelva el producto\nconst multiply = a => {\n  // Write your code here / Escribe tu código aquí\n\n};\n\n// Call multiply(2)(3)(4) and log it / Llama a multiply(2)(3)(4) e imprímelo\n`,
    validate: (logs, code) => {
      const hasCurrying = /multiply\(\d+\)\(\d+\)\(\d+\)/.test(code) || code.includes('multiply(');
      const has24 = logs.some(log => log.text.trim() === '24');
      return hasCurrying && has24;
    }
  },
  {
    id: 'free',
    template: `// Free Play Mode / Modo Código Libre\n// Try writing loops, async fetches, or algorithms!\n\nconsole.log("Welcome to the Playground!");\n`,
    validate: () => true
  }
]

function RunJSFrame() {
  const { t, i18n } = useTranslation()
  const isSpanish = i18n.language.startsWith('es')

  // UI State
  const [isInteractive, setIsInteractive] = useState(false)
  const [lessonsOpen, setLessonsOpen] = useState(true)
  const [consoleOpen, setConsoleOpen] = useState(true)
  const [activeTab, setActiveTab] = useState('editor') // 'lessons' | 'editor' | 'output'

  // Code Playground State
  const [activeLessonIdx, setActiveLessonIdx] = useState(0)
  const [codes, setCodes] = useState(() => {
    const defaultCodes = {}
    LESSONS.forEach(l => {
      defaultCodes[l.id] = l.template
    })
    return defaultCodes
  })
  const [logs, setLogs] = useState([])
  const [running, setRunning] = useState(false)
  const [completedLessons, setCompletedLessons] = useState(() => {
    try {
      const saved = localStorage.getItem('runjs:completed_lessons')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })

  // Typing animation states
  const [typedIdx, setTypedIdx] = useState(0)
  const [pause, setPause] = useState(false)
  const loopRef = useRef(null)

  const staticTokens = useMemo(() => tokenize(STATIC_CODE), [])
  const typedTokens = useMemo(() => tokenize(TYPED_CODE).flat().filter(Boolean), [])
  const typedChars = useMemo(() => buildTypedChars(typedTokens), [typedTokens])
  const staticTotalLines = useMemo(() => tokenize(STATIC_CODE + '\n' + TYPED_CODE).filter(Boolean).length, [])

  // Refs for Editor scroll sync
  const textareaRef = useRef(null)
  const preRef = useRef(null)

  const activeLesson = LESSONS[activeLessonIdx]
  const currentCode = codes[activeLesson.id]

  // Typing Animation Loop (only active when not interactive)
  useEffect(() => {
    if (isInteractive) return

    if (pause) {
      loopRef.current = setTimeout(() => {
        setTypedIdx(0)
        setPause(false)
      }, 3000)
      return () => clearTimeout(loopRef.current)
    }
    if (typedIdx >= typedChars.length) {
      setPause(true)
      return
    }
    loopRef.current = setTimeout(
      () => setTypedIdx(i => i + 1),
      30 + Math.random() * 40
    )
    return () => clearTimeout(loopRef.current)
  }, [typedIdx, pause, typedChars.length, isInteractive])

  // Clear typing timeout on unmount
  useEffect(() => {
    return () => {
      if (loopRef.current) clearTimeout(loopRef.current)
    }
  }, [])

  // Save completed lessons to localStorage
  const markLessonCompleted = useCallback((id) => {
    if (id === 'free') return
    setCompletedLessons(prev => {
      if (prev.includes(id)) return prev
      const next = [...prev, id]
      try {
        localStorage.setItem('runjs:completed_lessons', JSON.stringify(next))
      } catch (e) {
        console.error(e)
      }
      return next
    })
  }, [])

  // Transition to Interactive Mode
  const startInteractivity = useCallback((lessonIdx = 0) => {
    setIsInteractive(true)
    setActiveLessonIdx(lessonIdx)
    setActiveTab('editor')
    setLogs([])
  }, [])

  // Sync scroll positions
  const handleScroll = (e) => {
    if (preRef.current) {
      preRef.current.scrollTop = e.target.scrollTop
      preRef.current.scrollLeft = e.target.scrollLeft
    }
  }

  // Handle Code changes
  const handleCodeChange = (e) => {
    const val = e.target.value
    setCodes(prev => ({ ...prev, [activeLesson.id]: val }))
  }

  // Handle Tab key indent & Ctrl+Enter to Run
  const handleKeyDown = (e) => {
    if (!isInteractive) {
      startInteractivity()
      return
    }

    if (e.key === 'Tab') {
      e.preventDefault()
      const textarea = textareaRef.current
      if (!textarea) return
      const { selectionStart, selectionEnd } = textarea
      const val = codes[activeLesson.id]
      const newVal = val.substring(0, selectionStart) + '  ' + val.substring(selectionEnd)
      setCodes(prev => ({ ...prev, [activeLesson.id]: newVal }))

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = selectionStart + 2
      }, 0)
    }

    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      handleRun()
    }
  }

  // Execute User Code
  const handleRun = async () => {
    if (!isInteractive) {
      startInteractivity()
      return
    }

    setRunning(true)
    setLogs([])

    // Make sure Output tab is active on mobile
    setActiveTab('output')
    // Open console pane on desktop automatically when code is run
    setConsoleOpen(true)

    const executionLogs = []
    const customConsole = {
      log: (...args) => {
        const text = args.map(arg => {
          if (arg === null) return 'null'
          if (arg === undefined) return 'undefined'
          if (typeof arg === 'object') {
            try { return JSON.stringify(arg); } catch { return String(arg); }
          }
          return String(arg)
        }).join(' ')
        executionLogs.push({ type: 'log', text })
      },
      error: (...args) => {
        const text = args.map(String).join(' ')
        executionLogs.push({ type: 'error', text })
      },
      warn: (...args) => {
        const text = args.map(String).join(' ')
        executionLogs.push({ type: 'warn', text })
      }
    }

    try {
      // Support top-level await by wrapping in an async IIFE
      const wrapped = `
        const console = {
          log: _log,
          error: _error,
          warn: _warn,
          info: _log
        };
        return (async () => {
          ${codes[activeLesson.id]}
        })();
      `
      // eslint-disable-next-line no-new-func
      const runner = new Function('_log', '_error', '_warn', wrapped)
      await runner(customConsole.log, customConsole.error, customConsole.warn)
    } catch (err) {
      customConsole.error(err.message || err)
    }

    // Run validation if it is a structured lesson
    let validationPassed = false
    if (activeLesson.id !== 'free') {
      try {
        validationPassed = activeLesson.validate(executionLogs, codes[activeLesson.id])
      } catch (e) {
        validationPassed = false
      }
    }

    if (validationPassed) {
      markLessonCompleted(activeLesson.id)
      executionLogs.push({
        type: 'success',
        text: t('playground.lessons.successMsg', '✔ Objectives met! Click \'Next Lesson\' to continue.')
      })
    } else if (activeLesson.id !== 'free') {
      executionLogs.push({
        type: 'warn',
        text: t('playground.lessons.errorMsg', '✖ Objectives not met yet. Check the console and try again.')
      })
    }

    setLogs(executionLogs)
    setRunning(false)
  }

  // Reset current lesson code to template
  const handleReset = () => {
    setCodes(prev => ({ ...prev, [activeLesson.id]: activeLesson.template }))
    setLogs([])
  }

  const handleNextLesson = () => {
    if (activeLessonIdx < LESSONS.length - 1) {
      setActiveLessonIdx(activeLessonIdx + 1)
      setLogs([])
      setActiveTab('editor')
    }
  }

  const renderTyped = () => {
    let charPos = 0
    const visibleTokens = []
    let lastType = null
    let buffer = ''

    function flush() {
      if (buffer) { visibleTokens.push(T(lastType, buffer)); buffer = '' }
    }

    for (let i = 0; i < typedTokens.length && charPos < typedIdx; i++) {
      const t = typedTokens[i]
      const tokenLen = t.text.length
      const end = charPos + tokenLen
      if (end <= typedIdx) {
        if (t.type !== lastType) { flush(); lastType = t.type }
        buffer += t.text
        charPos = end
      } else {
        const partial = t.text.slice(0, typedIdx - charPos)
        flush()
        return { tokens: [...visibleTokens, { ...t, text: partial }] }
      }
    }
    flush()
    return { tokens: visibleTokens }
  }

  const typed = renderTyped()
  const awaitingTyping = typedIdx < typedChars.length

  // Tokenize user's interactive code
  const userHighlightedLines = useMemo(() => {
    return tokenize(currentCode)
  }, [currentCode])

  const getLessonTranslation = (lesson) => {
    if (lesson.id === 'free') {
      return {
        title: isSpanish ? 'Playground Libre' : 'Free Playground',
        desc: isSpanish 
          ? 'Escribe y ejecuta cualquier código JavaScript aquí. ¡Siéntete libre de experimentar sin restricciones!' 
          : 'Write and execute any JavaScript code here. Feel free to experiment with no restrictions!',
        objective: isSpanish 
          ? '¡Diviértete experimentando con JavaScript!' 
          : 'Have fun experimenting with JavaScript!'
      }
    }
    return {
      title: t(`playground.lessons.items.${lesson.id}.title`),
      desc: t(`playground.lessons.items.${lesson.id}.desc`),
      objective: t(`playground.lessons.items.${lesson.id}.objective`)
    }
  }

  const translatedLesson = getLessonTranslation(activeLesson)

  // Total lines of editor text
  const totalLines = isInteractive ? currentCode.split('\n').length : staticTotalLines

  return (
    <div className="rjf-backdrop">
      <div className={`rjf-frame ${isInteractive ? 'interactive' : ''}`}>
        {/* Title Bar */}
        <div className="rjf-titlebar">
          <div className="rjf-dots" onClick={() => startInteractivity(0)}>
            <span className="rjf-dot rjf-dot-red" />
            <span className="rjf-dot rjf-dot-yellow" />
            <span className="rjf-dot rjf-dot-green" />
          </div>
          
          {/* Tabs - Interactive on mobile & desktop */}
          <div className="rjf-tabs">
            <span className="rjf-tab-prefix">❯</span>
            
            {isInteractive ? (
              <>
                {/* Mobile and Desktop Tabs */}
                <span 
                  className={`rjf-tab rjf-mobile-only ${activeTab === 'lessons' ? 'rjf-tab-active' : ''}`}
                  onClick={() => setActiveTab('lessons')}
                >
                  {t('playground.lessons.title', 'Lessons')}
                </span>
                <span 
                  className={`rjf-tab ${activeTab === 'editor' ? 'rjf-tab-active' : ''}`}
                  onClick={() => {
                    setActiveTab('editor')
                    if (!consoleOpen && !lessonsOpen) {
                      // fallback for desktop if all panes are closed
                      setLessonsOpen(true)
                    }
                  }}
                >
                  {activeLesson.id === 'free' ? 'playground.js' : `lesson_${activeLesson.id}.js`}
                </span>
                <span 
                  className={`rjf-tab ${activeTab === 'output' ? 'rjf-tab-active' : ''}`}
                  onClick={() => {
                    setActiveTab('output')
                    setConsoleOpen(true)
                  }}
                >
                  output
                </span>
              </>
            ) : (
              <>
                <span className="rjf-tab rjf-tab-active">server.js</span>
                <span className="rjf-tab" onClick={() => startInteractivity(0)}>output</span>
              </>
            )}
          </div>
        </div>

        {/* Frame Body */}
        <div className="rjf-body">
          {/* Vertical Activity Bar */}
          <div className="rjf-activity">
            <div className="rjf-activity-group">
              <span 
                className={`rjf-activity-btn ${running ? 'active' : ''}`} 
                onClick={handleRun}
                title={t('playground.run')}
              >
                <PlayIcon />
              </span>
              <span 
                className="rjf-activity-btn" 
                onClick={() => {
                  if (isInteractive) {
                    handleReset()
                  } else {
                    startInteractivity()
                  }
                }}
                title={t('playground.lessons.reset', 'Reset Code')}
              >
                {isInteractive ? <ResetIcon /> : <StopIcon />}
              </span>
            </div>
            
            <hr className="rjf-activity-sep" />
            
            <div className="rjf-activity-group">
              <span 
                className={`rjf-activity-btn ${lessonsOpen && isInteractive ? 'active' : ''}`}
                onClick={() => {
                  if (!isInteractive) startInteractivity()
                  setLessonsOpen(!lessonsOpen)
                  if (activeTab === 'lessons' && lessonsOpen) setActiveTab('editor')
                  else if (!lessonsOpen) setActiveTab('lessons')
                }}
                title={t('playground.lessons.title', 'Lessons')}
              >
                <BookmarkIcon />
              </span>
              <span 
                className={`rjf-activity-btn ${consoleOpen && isInteractive ? 'active' : ''}`}
                onClick={() => {
                  if (!isInteractive) startInteractivity()
                  setConsoleOpen(!consoleOpen)
                  if (activeTab === 'output' && consoleOpen) setActiveTab('editor')
                  else if (!consoleOpen) setActiveTab('output')
                }}
                title="Console Output"
              >
                <TerminalIcon />
              </span>
              {isInteractive && (
                <span 
                  className="rjf-activity-btn rjf-danger-hover"
                  onClick={() => setLogs([])}
                  title="Clear Console"
                >
                  <TrashIcon />
                </span>
              )}
            </div>
          </div>

          {/* Lessons Sidebar (Interactive Only) */}
          {isInteractive && (
            <div className={`rjf-sidebar ${lessonsOpen ? 'open' : 'collapsed'} ${activeTab === 'lessons' ? 'mobile-visible' : ''}`}>
              <div className="rjf-sidebar-header">
                <h4>{t('playground.lessons.title', 'JS Lessons')}</h4>
              </div>
              
              {/* Checklist list */}
              <div className="rjf-lessons-list">
                {LESSONS.map((les, index) => {
                  const isCompleted = completedLessons.includes(les.id)
                  const isActive = activeLessonIdx === index
                  const translated = getLessonTranslation(les)
                  return (
                    <div 
                      key={les.id} 
                      className={`rjf-lesson-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                      onClick={() => {
                        setActiveLessonIdx(index)
                        setActiveTab('editor')
                      }}
                    >
                      <span className="rjf-lesson-status">
                        {isCompleted ? <CheckIcon /> : <CircleIcon />}
                      </span>
                      <span className="rjf-lesson-title-text">{translated.title}</span>
                    </div>
                  )
                })}
              </div>

              {/* Active Lesson Description */}
              <div className="rjf-lesson-detail">
                <h5>{translatedLesson.title}</h5>
                <p className="rjf-lesson-desc-p">{translatedLesson.desc}</p>
                <div className="rjf-lesson-objective-box">
                  <strong>{t('playground.lessons.objective', 'Objective:')}</strong>
                  <p>{translatedLesson.objective}</p>
                </div>

                {/* Sidebar Actions */}
                <div className="rjf-lesson-actions">
                  <span className={`rjf-icon-btn rjf-sidebar-run-btn ${running ? 'active' : ''}`} onClick={handleRun} title={t('playground.run')}>
                    <PlayIcon />
                  </span>
                  {completedLessons.includes(activeLesson.id) && activeLessonIdx < LESSONS.length - 1 && (
                    <span className="rjf-btn-next" onClick={handleNextLesson}>
                      {t('playground.lessons.next', 'Next Lesson')} &rarr;
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Code Editor and Output Panels container */}
          <div className="rjf-content-container">
            {/* Code Editor Panel */}
            <div className={`rjf-editor-pane ${activeTab === 'editor' ? 'mobile-visible' : ''}`}>
              {!isInteractive ? (
                // ENTRY ANIMATION VIEW
                <div className="rjf-editor" onClick={() => startInteractivity(0)}>
                  <pre><code>
                    {renderStatic(staticTokens)}
                    <span className="rjf-line">
                      {typed.tokens.map((t, i) => (
                        <span key={i} className={`rjf-${t.type}`}>{t.text}</span>
                      ))}
                      {awaitingTyping && <span className="rjf-cursor" />}
                    </span>
                  </code></pre>
                </div>
              ) : (
                // INTERACTIVE EDITOR VIEW
                <div className="rjf-editor-container">
                  <textarea
                    ref={textareaRef}
                    className="rjf-textarea"
                    value={currentCode}
                    onChange={handleCodeChange}
                    onKeyDown={handleKeyDown}
                    onScroll={handleScroll}
                    spellCheck={false}
                    autoCapitalize="none"
                    autoComplete="off"
                    autoCorrect="off"
                  />
                  <pre ref={preRef} className="rjf-highlighted-pre">
                    <code className="rjf-highlighted-code">
                      {userHighlightedLines.map((line, idx) => renderTokens(line, idx))}
                    </code>
                  </pre>
                </div>
              )}
            </div>

            {/* Console Output Panel (Interactive Only) */}
            {isInteractive && (
              <div className={`rjf-console-pane ${consoleOpen ? 'open' : 'collapsed'} ${activeTab === 'output' ? 'mobile-visible' : ''}`}>
                <div className="rjf-console-header">
                  <span>Console Output</span>
                  <span className="rjf-icon-btn rjf-console-clear-btn" onClick={() => setLogs([])} title={isSpanish ? 'Limpiar Consola' : 'Clear Console'}>
                    <TrashIcon />
                  </span>
                </div>
                <div className="rjf-console-logs">
                  {logs.length === 0 ? (
                    <div className="rjf-log-empty">
                      {isSpanish ? 'Esperando ejecución...' : 'Awaiting execution...'}
                    </div>
                  ) : (
                    logs.map((log, i) => (
                      <div key={i} className={`rjf-log-line rjf-log-${log.type}`}>
                        {log.type === 'success' && <span className="rjf-log-bullet">✔</span>}
                        {log.type === 'error' && <span className="rjf-log-bullet">✖</span>}
                        {log.type === 'warn' && <span className="rjf-log-bullet">⚠</span>}
                        <span className="rjf-log-text">{log.text}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Status Bar */}
        <div className="rjf-output">
          {isInteractive ? (
            <span className="rjf-output-text">
              {completedLessons.length === LESSONS.length - 1 ? (
                <span className="rjf-text-success font-bold">{t('playground.lessons.congrats')}</span>
              ) : (
                <span>
                  {isSpanish ? 'Progreso: ' : 'Progress: '}
                  {completedLessons.length} / {LESSONS.length - 1} {t('playground.lessons.completed')}
                </span>
              )}
            </span>
          ) : (
            <span className={typedIdx === 0 ? 'rjf-output-waiting' : 'rjf-output-text'}>
              {typedIdx === 0 ? 'Waiting...' : 'Ready'}
            </span>
          )}
          <span className="rjf-output-ln">{totalLines} {isSpanish ? 'líneas' : 'lines'}</span>
        </div>
      </div>
    </div>
  )
}

export default RunJSFrame
